import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getPresentationById } from '../services/presentationsService';
import { listSlidesByPresentation } from '../services/presentationSlidesService';
import { getViewProgress, upsertProgress } from '../services/presentationViewsService';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiChevronLeft, FiChevronRight, FiX } = FiIcons;

export default function PresentationViewer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { profile, authReady, loading: authLoading, profileLoading } = useAuth();
  const [presentation, setPresentation] = useState(null);
  const [slides, setSlides] = useState([]);
  const [index, setIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [savingProgress, setSavingProgress] = useState(false);

  const opts = profile?.id && profile?.role
    ? { role: profile.role, userId: profile.id }
    : {};

  const total = slides.length;
  const currentSlide = total > 0 ? slides[index] : null;

  const saveProgress = useCallback(
    async (lastSlideViewed, completed) => {
      if (!profile?.id || !id) return;
      setSavingProgress(true);
      await upsertProgress(id, { last_slide_viewed: lastSlideViewed, completed }, opts);
      setSavingProgress(false);
    },
    [id, profile?.id, opts]
  );

  useEffect(() => {
    if (!id || !authReady || !profile?.id || !profile?.role) return;

    let cancelled = false;
    (async () => {
      setLoading(true);
      const [presRes, slidesRes, viewRes] = await Promise.all([
        getPresentationById(id, opts),
        listSlidesByPresentation(id, opts),
        getViewProgress(id, opts)
      ]);

      if (cancelled) return;
      if (presRes.error || !presRes.data) {
        setPresentation(null);
        setSlides([]);
        setLoading(false);
        return;
      }

      setPresentation(presRes.data);
      setSlides(slidesRes.data || []);
      const view = viewRes.data;
      const startIndex = view?.last_slide_viewed ?? 0;
      setIndex(Math.min(Math.max(0, startIndex), (slidesRes.data?.length || 1) - 1));
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [id, authReady, profile?.id, profile?.role]);

  useEffect(() => {
    if (!id || total === 0 || !profile?.id) return;
    const completed = index >= total - 1 && total > 0;
    saveProgress(index, completed);
  }, [index, total, id, profile?.id]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') {
        setIndex((i) => Math.max(0, i - 1));
        e.preventDefault();
      } else if (e.key === 'ArrowRight') {
        setIndex((i) => Math.min(total - 1, i + 1));
        e.preventDefault();
      } else if (e.key === 'Escape') {
        navigate('/presentations');
        e.preventDefault();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [total, navigate]);

  if (!authReady || authLoading || profileLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-white border-t-transparent" />
      </div>
    );
  }

  if (!profile?.id || (profile.role !== 'agent' && profile.role !== 'admin')) {
    navigate('/dashboard');
    return null;
  }

  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-white border-t-transparent" />
      </div>
    );
  }

  if (!presentation) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-gray-900 text-white">
        <div className="text-center">
          <p className="text-lg">Presentation not found.</p>
          <button
            onClick={() => navigate('/presentations')}
            className="mt-4 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg"
          >
            Back to list
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 flex flex-col bg-gray-900 text-white">
      {/* Top bar: progress + exit */}
      <div className="flex-shrink-0 flex items-center justify-between px-4 py-2 bg-black/30">
        <div className="flex-1 h-2 max-w-md rounded-full bg-white/20 overflow-hidden">
          <div
            className="h-full bg-[#00AAFF] rounded-full transition-all duration-300"
            style={{ width: total ? `${((index + 1) / total) * 100}%` : '0%' }}
          />
        </div>
        <div className="mx-4 text-sm text-white/80 whitespace-nowrap">
          {index + 1} / {total || 0}
        </div>
        <button
          type="button"
          onClick={() => navigate('/presentations')}
          className="p-2 rounded-lg hover:bg-white/10 transition-colors"
          title="Exit"
        >
          <SafeIcon icon={FiX} className="w-5 h-5" />
        </button>
      </div>

      {/* Slide area */}
      <div className="flex-1 flex items-center justify-center overflow-hidden p-8">
        {!currentSlide ? (
          <p className="text-white/60">No slides yet.</p>
        ) : (
          <div className="w-full max-w-4xl max-h-full overflow-auto rounded-lg bg-black/40 flex items-center justify-center min-h-[300px]">
            {currentSlide.content_type === 'image' && currentSlide.content_url && (
              <img
                src={currentSlide.content_url}
                alt={currentSlide.title || ''}
                className="max-w-full max-h-[70vh] object-contain"
              />
            )}
            {currentSlide.content_type === 'video' && currentSlide.content_url && (
              <div className="w-full aspect-video max-h-[70vh]">
                <iframe
                  src={currentSlide.content_url}
                  title={currentSlide.title || ''}
                  className="w-full h-full rounded"
                  allowFullScreen
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                />
              </div>
            )}
            {currentSlide.content_type === 'pdf' && currentSlide.content_url && (
              <iframe
                src={currentSlide.content_url}
                title={currentSlide.title || ''}
                className="w-full h-[70vh] rounded"
              />
            )}
            {(currentSlide.content_type === 'html' || currentSlide.content_html) && (
              <div
                className="p-8 prose prose-invert max-w-none"
                dangerouslySetInnerHTML={{ __html: currentSlide.content_html || '<p>No content</p>' }}
              />
            )}
            {!currentSlide.content_url && !currentSlide.content_html && (
              <p className="text-white/60">No content for this slide.</p>
            )}
          </div>
        )}
      </div>

      {/* Bottom nav */}
      <div className="flex-shrink-0 flex items-center justify-center gap-4 py-4 bg-black/30">
        <button
          type="button"
          onClick={() => setIndex((i) => Math.max(0, i - 1))}
          disabled={index === 0}
          className="p-3 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <SafeIcon icon={FiChevronLeft} className="w-6 h-6" />
        </button>
        <span className="text-sm text-white/80">
          {index + 1} of {total || 0}
        </span>
        <button
          type="button"
          onClick={() => setIndex((i) => Math.min(total - 1, i + 1))}
          disabled={index >= total - 1}
          className="p-3 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <SafeIcon icon={FiChevronRight} className="w-6 h-6" />
        </button>
      </div>
    </div>
  );
}
